'use client'

import { useEffect, useRef, useState } from 'react'

import styles from './page.module.css'

type ConsentStatus = 'accepted' | 'declined' | 'pending'
type AnalyticsEventType =
    | 'visit_started'
    | 'visit_engaged'
    | 'form_interaction'
    | 'cookie_consent_accepted'
    | 'cookie_consent_declined'

type AnalyticsPayload = Readonly<{
    eventType: AnalyticsEventType
    metadata?: Record<string, string | number | boolean | null>
    consentStatus?: ConsentStatus
    visitorId?: string | null
    visitId?: string | null
}>

const consentStorageKey = 'gmax.analytics.consent'
const declinedSessionKey = 'gmax.analytics.declined'
const visitorStorageKey = 'gmax.analytics.visitorId'
const visitSessionKey = 'gmax.analytics.visitId'
const formInteractionSessionKey = 'gmax.analytics.formInteraction'

export function CookieConsentAnalytics() {
    const [bannerState, setBannerState] = useState<'visible' | 'hidden'>('visible')
    const startedAtRef = useRef<number>(0)
    const engagedSentRef = useRef(false)

    useEffect(() => {
        const sourceMetadata = readSourceMetadata()
        startedAtRef.current = Date.now()
        const storedConsent = readStoredConsent()

        if (storedConsent !== 'pending') {
            window.setTimeout(() => {
                setBannerState('hidden')
            }, 0)
        }

        void sendAnalyticsEvent(
            {
                eventType: 'visit_started',
                ...readIdentifiers(storedConsent),
                consentStatus: storedConsent,
                metadata: {}
            },
            sourceMetadata
        )
    }, [])

    useEffect(() => {
        function handleFormFocus(event: FocusEvent) {
            const target = event.target

            if (!(target instanceof Element)) {
                return
            }

            const form = target.closest<HTMLFormElement>('form[data-analytics-form]')

            if (form === null) {
                return
            }

            const formKey = form.dataset.analyticsForm ?? 'unknown'
            const storageKey = `${formInteractionSessionKey}.${formKey}`

            if (window.sessionStorage.getItem(storageKey) === 'sent') {
                return
            }

            window.sessionStorage.setItem(storageKey, 'sent')
            void sendAnalyticsEvent(
                {
                    eventType: 'form_interaction',
                    ...readIdentifiers(readStoredConsent()),
                    consentStatus: readStoredConsent(),
                    metadata: {
                        formKey
                    }
                },
                readSourceMetadata()
            )
        }

        document.addEventListener('focusin', handleFormFocus)

        return () => {
            document.removeEventListener('focusin', handleFormFocus)
        }
    }, [])

    useEffect(() => {
        function trySendEngagement() {
            if (engagedSentRef.current) {
                return
            }

            const status = readStoredConsent()
            const identifiers = readIdentifiers(status)
            const timeOnPageSeconds = Math.floor((Date.now() - startedAtRef.current) / 1000)
            const scrollY = window.scrollY
            const viewportHeight = window.innerHeight

            if (timeOnPageSeconds < 15 || viewportHeight <= 0 || scrollY <= viewportHeight * 0.9) {
                return
            }

            engagedSentRef.current = true
            void sendAnalyticsEvent(
                {
                    eventType: 'visit_engaged',
                    ...identifiers,
                    consentStatus: status,
                    metadata: {
                        timeOnPageSeconds,
                        scrollY,
                        viewportHeight
                    }
                },
                readSourceMetadata()
            )
        }

        const intervalId = window.setInterval(trySendEngagement, 1000)
        window.addEventListener('scroll', trySendEngagement, { passive: true })

        return () => {
            window.clearInterval(intervalId)
            window.removeEventListener('scroll', trySendEngagement)
        }
    }, [])

    function acceptConsent() {
        const identifiers = ensureAcceptedIdentifiers()
        window.localStorage.setItem(consentStorageKey, 'accepted')
        window.sessionStorage.removeItem(declinedSessionKey)
        setBannerState('hidden')
        void sendAnalyticsEvent(
            {
                eventType: 'cookie_consent_accepted',
                ...identifiers,
                consentStatus: 'accepted',
                metadata: {}
            },
            readSourceMetadata()
        )
    }

    function declineConsent() {
        window.localStorage.removeItem(consentStorageKey)
        window.localStorage.removeItem(visitorStorageKey)
        window.sessionStorage.removeItem(visitSessionKey)
        window.sessionStorage.setItem(declinedSessionKey, 'true')
        setBannerState('hidden')
        void sendAnalyticsEvent(
            {
                eventType: 'cookie_consent_declined',
                visitorId: null,
                visitId: null,
                consentStatus: 'declined',
                metadata: {}
            },
            readSourceMetadata()
        )
    }

    if (bannerState !== 'visible') {
        return null
    }

    return (
        <aside
            aria-label="Cookie consent"
            className={styles.cookieConsent}
            data-consent-status="pending"
        >
            <div className={styles.cookieConsentCopy}>
                <strong>Analytics cookies</strong>
                <p>
                    We use anonymous analytics to understand visits, form interactions, and site
                    performance. Declining keeps this visit anonymous and stores no analytics
                    identifiers.
                </p>
            </div>
            <div className={styles.cookieConsentActions}>
                <button className={styles.secondaryButton} type="button" onClick={declineConsent}>
                    Decline
                </button>
                <button className={styles.primaryButton} type="button" onClick={acceptConsent}>
                    Accept
                </button>
            </div>
        </aside>
    )
}

function readStoredConsent(): ConsentStatus {
    if (window.localStorage.getItem(consentStorageKey) === 'accepted') {
        return 'accepted'
    }

    if (window.sessionStorage.getItem(declinedSessionKey) === 'true') {
        return 'declined'
    }

    return 'pending'
}

function readIdentifiers(consentStatus: ConsentStatus) {
    if (consentStatus !== 'accepted') {
        return {
            visitorId: null,
            visitId: null
        }
    }

    return ensureAcceptedIdentifiers()
}

function ensureAcceptedIdentifiers() {
    let visitorId = window.localStorage.getItem(visitorStorageKey)
    let visitId = window.sessionStorage.getItem(visitSessionKey)

    if (visitorId === null) {
        visitorId = createIdentifier()
        window.localStorage.setItem(visitorStorageKey, visitorId)
    }

    if (visitId === null) {
        visitId = createIdentifier()
        window.sessionStorage.setItem(visitSessionKey, visitId)
    }

    return {
        visitorId,
        visitId
    }
}

function createIdentifier(): string {
    if (window.crypto.randomUUID !== undefined) {
        return window.crypto.randomUUID()
    }

    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes)

    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function sendAnalyticsEvent(payload: AnalyticsPayload, sourceMetadata: SourceMetadata) {
    try {
        await fetch('/api/analytics', {
            method: 'POST',
            keepalive: true,
            headers: {
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                consentStatus: payload.consentStatus ?? 'pending',
                visitorId: payload.visitorId ?? null,
                visitId: payload.visitId ?? null,
                metadata: payload.metadata ?? {},
                occurredAt: new Date().toISOString(),
                ...payload,
                ...sourceMetadata
            })
        })
    } catch {
        // Analytics must never interrupt public browsing or lead submission.
    }
}

type SourceMetadata = Readonly<{
    sourcePage: string
    utmSource: string | null
    utmMedium: string | null
    utmCampaign: string | null
    referrer: string | null
    clientLocale: string | null
    clientUserAgent: string | null
}>

function readSourceMetadata(): SourceMetadata {
    const params = new URLSearchParams(window.location.search)

    return {
        sourcePage: window.location.pathname,
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
        referrer: document.referrer || null,
        clientLocale: navigator.language || null,
        clientUserAgent: navigator.userAgent || null
    }
}
